import React from "react";
import { Box } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import moment from "moment-timezone";
import Row from "visual-components/util/no-padding-grid/row";
import MediaDateRangePicker from "./media-date-range-picker";

/**
 * Media Details Graph Component using MUI X Charts
 * Displays line chart for media sell details
 * @param {Object} props
 * @param {Array} props.dataPoints - Array of data points with period and y values
 * @param {boolean} props.hourly - Whether to format as hourly data
 * @param {string} props.selectedTab - Currently selected tab
 * @param {boolean} props.cumulative - Whether showing cumulative data
 * @param {boolean} props.isFixedDeal - Whether this is a fixed deal
 * @param {Object} props.minMaxThreshold - Min/max threshold values
 * @param {Date} props.startDate - Start date
 * @param {Date} props.endDate - End date
 * @param {string} props.timeFrame - Time frame setting
 * @param {Function} props.selectDate - Date selection handler
 * @param {Date} props.dealStartDate - Deal start date
 * @param {Date} props.dealEndDate - Deal end date
 * @returns {import("react").ReactElement}
 */
export default function MediaDetailsGraph(props) {
  const {
    dataPoints = [],
    hourly,
    startDate,
    endDate,
    timeFrame,
    selectDate,
    dealStartDate,
    dealEndDate,
  } = props;

  if (!dataPoints || dataPoints.length === 0) {
    return (
      <Row style={{ marginTop: "16px" }}>
        <Box sx={{ width: 604, height: 184, pb: 1 }}>
          <Box sx={{ width: 600, height: 300 }} />
        </Box>
        <Box sx={{ mt: 14.375 }}>
          <MediaDateRangePicker
            startDate={startDate}
            dealStartDate={dealStartDate}
            dealEndDate={dealEndDate}
            endDate={endDate}
            timeFrame={timeFrame}
            selectDateFunc={selectDate}
          />
        </Box>
      </Row>
    );
  }

  const dates = dataPoints.map((item) => item.period);
  const values = dataPoints.map((item) => item.y);

  // Create x-axis data as indices
  const xAxisData = dates.map((_, index) => index);

  // Format dates for x-axis labels
  const formattedDates = dates.map((date) => {
    if (hourly) {
      return moment(date).format("MMM DD, h a");
    }
    return moment(date).format("MMM DD");
  });

  return (
    <Row style={{ marginTop: "16px" }}>
      <Box sx={{ width: 604, height: 184, pb: 1 }}>
        <LineChart
          xAxis={[
            {
              data: xAxisData,
              scaleType: "point",
              valueFormatter: (value) => formattedDates[value] || "",
              tickLabelStyle: {
                fontSize: 10,
              },
            },
          ]}
          yAxis={[
            {
              disableLine: true,
            },
          ]}
          series={[
            {
              data: values,
              color: "#03a9f4",
              showMark: false,
              curve: "linear",
              valueFormatter: (value) => value?.toLocaleString() || "0",
            },
          ]}
          width={600}
          height={300}
          margin={{ top: 20, right: 20, bottom: 30, left: 50 }}
          hideLegend
        />
      </Box>
      <Box sx={{ mt: 14.375 }}>
        <MediaDateRangePicker
          startDate={startDate}
          dealStartDate={dealStartDate}
          dealEndDate={dealEndDate}
          endDate={endDate}
          timeFrame={timeFrame}
          selectDateFunc={selectDate}
        />
      </Box>
    </Row>
  );
}
